import { Inject, Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogService } from '@serviceplan/ui-kit';
import { DownloadUploadService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  AssetsGenerateUrlService,
  CanPipe,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  DataAccessType,
  ENVIRONMENT,
  FilterAllOptions,
  NotificationService,
  SpDateFormatPipe,
  StorageUtilityLib,
  USER_SESSION_TOKEN,
  UserSession,
} from '@thehubz/client/core';
import {
  Asset,
  AssetHistoryApiService,
  AssetsApiService,
  BuyoutRightFields,
  Collection,
  CreateAssetHistoryDto,
  Tags,
  TagsApiService,
  Territories,
  UpdateAssetDto,
  UserValue,
  UsersApiService,
} from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, BuyoutRightsMultiEditPayload, BuyoutRightsPayload, TerritoryMap, legacyIdX } from '@thehubz/shared/model';
import { BehaviorSubject, Observable, ReplaySubject, Subject, lastValueFrom, map, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AssetCommonService {
  readonly brandingKey = this.env.brandingKey;

  assetDataSubject$ = new ReplaySubject<AssetUIModel | null>(1);
  collectionRemoveAsset$ = new BehaviorSubject<{ assetID?: string }[]>([{}]);
  selectedFilterDataSubject$ = new BehaviorSubject<AnyType>({});

  userACBSubject$ = new BehaviorSubject<UserValue[]>([]);
  assetIDSubject = new Subject<boolean>();
  metadataEditable = new BehaviorSubject<AnyType>(null);
  assetDownloadExcel = new Subject<boolean>();
  assetDownloadHistoryCount = new BehaviorSubject<number>(0);
  exportAssetInfo = new Subject<boolean>();
  assetPlannedDeletion$ = new Subject<AssetUIModel>();
  openUpdatedPanel$ = new BehaviorSubject<number>(0);
  unMatchedUsageRight$ = new BehaviorSubject<string[]>([]);

  private _storedAssetsList: Array<AssetUIModel> = [];
  private _currentUser?: UserSession;
  private _userAgencyName?: string;

  constructor(
    @Inject(ENVIRONMENT) private readonly env: ClientEnvironment,
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private router: Router,
    private spDatePipe: SpDateFormatPipe,
    private assetService: AssetsApiService,
    private tagsApiService: TagsApiService,
    private activatedRoute: ActivatedRoute,
    private usersApiService: UsersApiService,
    private commonUtilityLib: CommonUtilityLib,
    private confirmDialog: ConfirmDialogService,
    private storageUtility: StorageUtilityLib,
    private notificationsService: NotificationService,
    private assetUrlService: AssetsGenerateUrlService,
    private downloadUploadService: DownloadUploadService,
    private assetHistoryApiService: AssetHistoryApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {}

  get currentUser(): UserSession {
    if (!this._currentUser) {
      this._currentUser = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken);
    }
    return this._currentUser;
  }

  getMainUrl(assetId: string, versionString: string, fileName: string) {
    const ext = fileName.split('.').pop();
    return `${this.env.fileAccessUrl}/assets/${assetId}/${versionString}/main.${ext}`;
  }

  onClick(action: string, data: AnyType) {
    if (action === 'createdBy' && data?.createdBy) {
      const userId = typeof data.createdBy === 'string' ? data.createdBy : data.createdBy.id;
      this.router.navigate(['user', 'profile'], { queryParams: { userId } });
    }
    if (action === 'updatedBy' && data?.updatedBy) {
      const userId = typeof data.updatedBy === 'string' ? data.updatedBy : data.updatedBy.id;
      this.router.navigate(['user', 'profile'], { queryParams: { userId } });
    }
  }

  deleteAsset(id: string) {
    this.confirmDialog
      .open({
        title: $localize`Delete asset`,
        message: $localize`Are you sure you want to delete this asset?`,
      })
      .subscribe((confirm) => {
        if (confirm) {
          this.assetsRemoveMultiple(id);
        }
      });
  }

  assetsRemoveMultiple(id: string) {
    this.assetService.assetsControllerRemoveMultiple(true, { assetIds: [id] }).subscribe({
      next: (res: AnyType) => {
        if (res.status === 'success') {
          this.clientNotificationHandlerService.successNotification($localize`Asset deleted successfully`);
          this.router.navigate(['/assets']);
          this.assetHistoryApiService.assetHistoryControllerRemoveMultiple(JSON.stringify({ filters: { downloadedAsset: { $eq: id } } })).subscribe();
        } else {
          this.notificationsService.addNotification({
            type: 'failure',
            message: res.details.length ? res.details[0].status : $localize`Something went wrong`,
          });
        }
      },
      error: (err) => this.clientNotificationHandlerService.errorNotification(err),
    });
  }

  assetsRemoveAssetVersion(assetInfo: AssetUIModel, assetId: string, assetVersionId: string) {
    this.assetService.assetsControllerDeleteAssetVersion(assetId, assetVersionId).subscribe({
      next: (res: AssetUIModel) => {
        if (res) {
          assetInfo.versions = res.versions;
          this.assetDataSubject$.next(assetInfo);
          this.clientNotificationHandlerService.successNotification($localize`Asset version deleted successfully`);
        } else {
          this.notificationsService.addNotification({
            type: 'failure',
            message: $localize`Something went wrong`,
          });
        }
      },
      error: (err) => this.clientNotificationHandlerService.errorNotification(err),
    });
  }

  plannedForDeletion(assetID: string, plannedDeletionValues: UpdateAssetDto, deletionDate: string) {
    this.assetService.assetsControllerUpdate(assetID, plannedDeletionValues).subscribe({
      next: (res) => {
        if (res) {
          const assetInfo = this.assetUrlService.addPreviewUrl(res);
          this.assetPlannedDeletion$.next(assetInfo);
          this.assetDataSubject$.next(assetInfo);
          this.clientNotificationHandlerService.successNotification($localize`This asset going to be deleted on ${deletionDate}`);
        }
      },
      error: (err) => this.clientNotificationHandlerService.errorNotification(err),
    });
  }

  deleteFileFormat(assetId: string, fileName: string, fileKey: string) {
    return this.confirmDialog
      .open({
        title: $localize`Delete file format`,
        message: $localize`Are you sure you want to delete file ${fileName}? It can't be restored, once deleted.`,
      })
      .pipe(
        switchMap((confirm) => {
          if (confirm) {
            return this.assetService.assetsControllerDeleteFileFormat(assetId, fileKey);
          }
          return of();
        }),
      );
  }

  checkCollectionOwner(collectionInfo: Collection): boolean {
    const currentOwners: string[] = JSON.parse(JSON.stringify(collectionInfo.ownerIDs || []));
    const collectionCreatedBy = legacyIdX(collectionInfo.createdBy);
    currentOwners.push(collectionCreatedBy);
    return currentOwners.some((owner) => owner === this.currentUser?.userData?._id);
  }

  deleteAspectRatio(assetId: string, versionId: string, aspect: string) {
    return this.confirmDialog
      .open({
        title: $localize`Delete asset aspect`,
        message: $localize`Are you sure you want to delete the aspect? It can't be restored, once deleted.`,
      })
      .pipe(
        switchMap((confirm) => {
          if (confirm) {
            return this.assetService.assetsControllerDeleteAspectRatio(assetId, versionId, aspect);
          }
          return of();
        }),
      );
  }

  async downloadAsset(assetInfo: AssetUIModel, module?: string, extraProp?: { channels?: string; licenseRegion?: string }, termsChecked?: boolean) {
    return this.downloadAssetVersion(assetInfo, assetInfo.mainFile.name, assetInfo.mainFile.version, module, extraProp, termsChecked);
  }

  async downloadAssetVersion(
    assetInfo: AssetUIModel,
    name: string,
    version?: number,
    module?: string,
    extraProp?: { channels?: string; licenseRegion?: string },
    termsChecked?: boolean,
  ) {
    let source;
    if (version || assetInfo.assetType === Asset.AssetTypeEnum.HtmlBanner) {
      source = this.assetUrlService.getDownloadUrl(assetInfo, version);
    } else {
      source = assetInfo.mainUrl || this.assetUrlService.getDownloadUrl(assetInfo);
    }
    const msg = this.clientNotificationHandlerService.successNotification($localize`Please wait. the asset is being downloaded`);
    try {
      let assetDownloadHistory: CreateAssetHistoryDto | undefined;
      if (module) {
        assetDownloadHistory = await this.assetDownloadHistory(assetInfo, module, version);
        if (extraProp) {
          assetDownloadHistory.channel = extraProp.channels;
          assetDownloadHistory.licenseRegion = extraProp.licenseRegion;
        }
        await this.downloadUploadService.downloadFile(source, name, assetDownloadHistory, termsChecked);
      } else {
        await this.downloadUploadService.downloadFile(source, name);
      }
    } catch (err) {
      if (msg) {
        msg.dismiss();
      }
      this.clientNotificationHandlerService.errorStringNotification($localize`Asset download failed`);
    }
  }

  /**
   * Create history object for asset download
   * @param assetInfo
   * @param module
   * @param version
   * @param fileExtension
   * @returns
   */
  async assetDownloadHistory(
    assetInfo: AssetUIModel,
    module: string,
    version?: number,
    fileExtension?: string,
    fileNameRef?: string,
  ): Promise<CreateAssetHistoryDto> {
    let defaultAspectRatio: string | undefined;
    this.queryParamsAspectRatio()
      .pipe(tap((params) => (defaultAspectRatio = params)))
      .subscribe();
    const aspectRatio = defaultAspectRatio || assetInfo.currentVersion?.aspectRatios?.[0].aspectRatio;
    const variant = assetInfo.currentVersion?.aspectRatios?.find((ratio) => ratio.aspectRatio === aspectRatio);
    const agency = await this.getCurrentUserAgencyName();
    return {
      downloadedAsset: assetInfo._id,
      version: version || assetInfo.currentVersion?.version || assetInfo.mainFile?.version || 1,
      agency,
      variant: variant && variant.aspectRatioName ? `Aspect ratio: ${variant.aspectRatioName}` : fileNameRef ? fileNameRef : 'Original asset',
      format: fileExtension?.toLocaleUpperCase() || assetInfo.mainFile.ext.toLocaleUpperCase(),
      module: module,
    };
  }

  updateAssetList(newList: AssetUIModel[]) {
    this._storedAssetsList = newList;
  }

  get storedAssetsList() {
    return this._storedAssetsList;
  }

  /**
   * Get icons based on types
   * @param assetType
   * @returns
   */
  getIcon(assetType: string) {
    switch (assetType) {
      case Asset.AssetTypeEnum.HtmlBanner:
        return 'html5_icon';
      case Asset.AssetTypeEnum.Zip:
        return 'zip_icon';
      case Asset.AssetTypeEnum.Image:
        return 'image_icon';
      case Asset.AssetTypeEnum.Video:
        return 'video_icon';
      case Asset.AssetTypeEnum.PowerpointPresentation:
        return 'powerpoint_icon';
      case Asset.AssetTypeEnum.Pdf:
        return 'pdf_icon';
      case Asset.AssetTypeEnum.Audio:
        return 'audio_icon';
      default:
        return 'file_icon';
    }
  }

  getStatusIcon(status: string, brandingKey: string) {
    const clientStatusIconMapping: AnyType = {
      [CustomClients.Vorwerk]: 'VW',
      [CustomClients.LufthansaMediacenter]: 'LH',
      [CustomClients.LufthansaMedialounge]: 'LH',
      [CustomClients.SEAT]: 'Seat',
      [CustomClients.VKB]: 'VKB',
    };
    switch (status) {
      case Asset.StatusEnum.Uploaded:
        return brandingKey && brandingKey != CustomClients.Standard ? clientStatusIconMapping[brandingKey] + '_asset_uploaded_icon' : 'asset_uploaded_icon';
      case Asset.StatusEnum.AwaitingApproval:
        return brandingKey && brandingKey != CustomClients.Standard
          ? clientStatusIconMapping[brandingKey] + '_asset_awaiting_approval_icon'
          : 'asset_awaiting_approval_icon';
      default:
        return '';
    }
  }
  /**
   * Get a range of number
   * @param start
   * @param range
   * @returns
   */
  numberRange(
    start: number,
    range: number,
  ): {
    id: number;
    name: number;
  }[] {
    return [...Array(range)].map((_, i) => {
      return { id: start + i, name: start + i };
    });
  }

  /**
   * Get expiry date of asset
   * @param date
   * @returns number
   */
  getAssetExpireDays(date?: string): number {
    if (!date) return 0;
    const expiryDate = new Date(date);
    const today = new Date();
    // Set the hours, minutes, seconds, and milliseconds to zero for accurate date comparison
    expiryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const totalDays: number = (expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24) + 1;
    const comparedTotal = Math.round(totalDays) <= AppSettings.SHOW_EXPIRY_ASSET_IN_DAYS ? Math.round(totalDays) : 0;
    // checking for today date (new Date(date).getDate() === new Date().getDate()) if true then send 1
    return expiryDate.getTime() === today.getTime() ? 1 : comparedTotal;
  }

  /**
   * Get aspect ration param value
   * @returns
   */
  queryParamsAspectRatio(): Observable<string> {
    return this.activatedRoute.queryParams.pipe(map((params) => params['aspectRatio']));
  }

  async getCurrentUserAgencyName() {
    if (!this._userAgencyName) {
      if (this.currentUser?.userData?._id) {
        const user = await lastValueFrom(this.usersApiService.usersControllerFindOne(this.currentUser.userData._id));
        this._userAgencyName = user.agency?.name ?? 'unknown';
      }
      this._userAgencyName = 'unknown';
    }
    return this._userAgencyName;
  }

  createFilterObj(nameObj: AnyType) {
    const filterObject: AnyType = {};
    filterObject['filters'] = { ...nameObj };
    filterObject['projection'] = { name: 1 };
    return filterObject;
  }

  async modifiedTags(tags: string[] | string, tagsInfo: Tags[] = []) {
    if (!tags || tags === '' || (tags.length && tags.length === 0)) return [];
    if (typeof tags === 'string') {
      tags = [tags];
    }

    const tagsObj: AnyType = tags.map((tag) => ({ name: tag }));
    const filterObject = this.createFilterObj({ name: { $in: tags } });
    try {
      const newTags: string[] = [
        ...(await lastValueFrom(this.tagsApiService.tagsControllerFindAll(JSON.stringify(filterObject)))).map((tag) => tag._id),
        ...(await lastValueFrom(this.tagsApiService.tagsControllerCreateMany(tagsObj))).map((tag) => tag._id),
        ...tagsInfo.map((tag: AnyType) => tag._id),
      ];
      // remove duplicates
      return [...new Set(newTags)];
    } catch (err) {
      this.clientNotificationHandlerService.errorNotification(err);
      throw err;
    }
  }

  patchFormValues(asset: AssetUIModel, formGroup: UntypedFormGroup) {
    if (asset) {
      const formValues = Object.keys(formGroup.value);

      formValues.forEach((field) => {
        asset.customFields?.[field] ? formGroup.get(field)?.setValue(asset.customFields[field]) : formGroup.get(field)?.setValue(asset[field]);
      });
      formGroup.get('updatedAt')?.setValue(asset.updatedAt ? this.spDatePipe.transform(asset.updatedAt) : '');
      formGroup.get('createdAt')?.setValue(asset.createdAt ? this.spDatePipe.transform(asset.createdAt) : '');
    }
  }

  transformAndFilterKeys(inputObj: AnyType, excludeKeys: string[] = []) {
    const customFields: Record<string, AnyType> = {};

    Object.keys(inputObj || {}).forEach((key) => {
      const value = inputObj[key];
      if (value !== null && Object.prototype.hasOwnProperty.call(inputObj, key) && !excludeKeys.includes(key)) {
        if (Array.isArray(value)) {
          const arrayKeys: string[] = [];
          value.forEach((val: AnyType) => {
            const value = val._id ?? val.id ?? val;
            if (value) {
              arrayKeys.push(value);
            }
          });
          customFields[key] = arrayKeys;
        } else {
          customFields[key] = value._id ?? value.id ?? value;
        }
        delete inputObj[key];
      }
      if (value === null && !excludeKeys.includes(key)) {
        customFields[key] = '';
        delete inputObj[key];
      }
      if (!value && !excludeKeys.includes(key)) {
        delete inputObj[key];
      }
    });

    return customFields;
  }

  transformVorwerkDomains(domains: AnyType) {
    const output = [];

    for (const key in domains) {
      if (key in domains) {
        const item = domains[key];
        const transformedItem = {} as { [key: string]: string };

        for (const innerKey in item) {
          if (innerKey in item) {
            transformedItem[innerKey] = item[innerKey]._id || item[innerKey];
          }
        }

        output.push(transformedItem);
      }
    }

    return output;
  }

  updateArvalFormData(formFieldName: string, customFieldsFormData: AnyType) {
    switch (formFieldName) {
      case 'product': {
        this.findFormFields('channel', true, customFieldsFormData);
        break;
      }
      case 'channel': {
        this.findFormFields('type', true, customFieldsFormData);
        break;
      }
    }
  }

  findFormFields = (findKey: string, visible: boolean, customFieldsFormData: AnyType) => {
    const formFieldIndex = customFieldsFormData.formFields.findIndex((field: AnyType) => field.formFieldName === findKey);
    if (formFieldIndex !== -1) customFieldsFormData.formFields[formFieldIndex].visible = visible;
  };

  /**
   * Get asset status list based on user roles
   * @param canPipe
   * @param anyAsset
   * @returns
   */
  getAssetStatusList(canPipe: CanPipe, anyAsset: DataAccessType, filterOptions?: FilterAllOptions): Asset.StatusEnum[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
      uploadedNAwaitingApproval: canPipe.transform(anyAsset, 'uploadednawaitingapproval'),
      publishedAssets: canPipe.transform(anyAsset, 'publishedassets'),
    };

    const assetStatusList: Asset.StatusEnum[] = [];

    if (this.env.brandingKey === CustomClients.LufthansaMediacenter || this.env.brandingKey === CustomClients.LufthansaMedialounge) {
      if (userRoles.publishedAssets) {
        if (filterOptions) this.applyPublishDateFilter(filterOptions);
        assetStatusList.push(Asset.StatusEnum.Approved);
      }

      if (userRoles.uploadedNAwaitingApproval) {
        this.deletePublishDateAndQuery(filterOptions);
        assetStatusList.push(Asset.StatusEnum.Uploaded);
      }

      if (userRoles.updateAllStatus) {
        this.deletePublishDateAndQuery(filterOptions);
        assetStatusList.push(Asset.StatusEnum.Uploaded, Asset.StatusEnum.Approved);
      }
    } else {
      if (userRoles.publishedAssets) {
        if (filterOptions) this.applyPublishDateFilter(filterOptions);
        assetStatusList.push(Asset.StatusEnum.Approved);
      }

      if (userRoles.uploadedNAwaitingApproval) {
        this.deletePublishDateAndQuery(filterOptions);
        assetStatusList.push(Asset.StatusEnum.Uploaded, Asset.StatusEnum.AwaitingApproval);
      }

      if (userRoles.updateAllStatus) {
        this.deletePublishDateAndQuery(filterOptions);
        assetStatusList.push(Asset.StatusEnum.Uploaded, Asset.StatusEnum.AwaitingApproval, Asset.StatusEnum.Approved);
      }
    }

    // Remove duplicates from the assetStatusList
    const uniqueAssetStatusList = Array.from(new Set(assetStatusList));

    return uniqueAssetStatusList;
  }

  /**
   * Deletes the '$and' filter from the provided filterOptions object if it exists and is not empty.
   * @param filterOptions
   */
  deletePublishDateAndQuery(filterOptions?: FilterAllOptions) {
    if (filterOptions && filterOptions.filters['$and'] && filterOptions.filters['$and'].length) delete filterOptions.filters['$and'];
  }

  applyPublishDateFilter(filterOptions: FilterAllOptions) {
    const currentDate = new Date();
    const startDateFilter = {
      $or: [{ publishStartDate: { $lte: currentDate } }, { publishStartDate: null }, { publishStartDate: { $exists: false } }],
    };
    const endDateFilter = {
      $or: [{ publishEndDate: { $gte: currentDate } }, { publishEndDate: { $exists: false } }, { publishEndDate: null }],
    };
    filterOptions['filters']['$and'] = [startDateFilter, endDateFilter];
  }

  /**
   * Get asset status list for filter based on user roles
   * @param canPipe
   * @param anyAsset
   * @returns
   */
  getAssetFilterStatusList(canPipe: CanPipe, anyAsset: DataAccessType, callFrom?: string): { id: Asset.StatusEnum; name: string }[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
      uploadedNAwaitingApproval: canPipe.transform(anyAsset, 'uploadednawaitingapproval'),
      publishedAssets: canPipe.transform(anyAsset, 'publishedassets'),
      archived: canPipe.transform(anyAsset, 'archived'),
      draft: canPipe.transform(anyAsset, 'draft'),
    };

    const assetStatusListObj: { id: Asset.StatusEnum; name: string }[] = [];

    if (userRoles.updateAllStatus) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Uploaded,
          name: $localize`Uploaded`,
        },
        {
          id: Asset.StatusEnum.AwaitingApproval,
          name: $localize`Awaiting approval`,
        },
        {
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        },
      );
    }

    if (userRoles.uploadedNAwaitingApproval) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Uploaded,
          name: $localize`Uploaded`,
        },
        {
          id: Asset.StatusEnum.AwaitingApproval,
          name: $localize`Awaiting approval`,
        },
      );
    }

    if (userRoles.publishedAssets) {
      if (callFrom === 'filters') {
        assetStatusListObj.push({
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        });
      }
    }

    if (userRoles.archived) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Archived,
        name: $localize`Archived`,
      });
    }

    if (userRoles.draft) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Draft,
        name: $localize`Draft`,
      });
    }

    // Remove duplicates from the assetStatusListObj
    const uniqueObjectsMap = new Map<string, AnyType>();
    assetStatusListObj.forEach((obj) => {
      uniqueObjectsMap.set(obj.id, obj);
    });
    const uniqueAssetStatusList = Array.from(uniqueObjectsMap.values());

    return uniqueAssetStatusList;
  }

  getVorwerkAssetFilterStatusList(canPipe: CanPipe, anyAsset: DataAccessType, callFrom?: string): { id: Asset.StatusEnum; name: string }[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
      uploadedNAwaitingApproval: canPipe.transform(anyAsset, 'uploadednawaitingapproval'),
      publishedAssets: canPipe.transform(anyAsset, 'publishedassets'),
      archived: canPipe.transform(anyAsset, 'archived'),
    };

    const assetStatusListObj: { id: Asset.StatusEnum; name: string }[] = [];

    if (userRoles.updateAllStatus) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Uploaded,
          name: $localize`Uploaded`,
        },
        {
          id: Asset.StatusEnum.AwaitingApproval,
          name: $localize`Awaiting approval`,
        },
        {
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        },
      );
    }

    if (userRoles.uploadedNAwaitingApproval) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Uploaded,
          name: $localize`Uploaded`,
        },
        {
          id: Asset.StatusEnum.AwaitingApproval,
          name: $localize`Awaiting approval`,
        },
      );
    }

    if (userRoles.publishedAssets) {
      if (callFrom === 'filters') {
        assetStatusListObj.push({
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        });
      }
    }

    if (userRoles.archived) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Archived,
        name: $localize`Archived`,
      });
    }

    // Remove duplicates from the assetStatusListObj
    const uniqueObjectsMap = new Map<string, AnyType>();
    assetStatusListObj.forEach((obj) => {
      uniqueObjectsMap.set(obj.id, obj);
    });
    const uniqueAssetStatusList = Array.from(uniqueObjectsMap.values());

    return uniqueAssetStatusList;
  }

  /**
   * Arval
   * Get asset status list for filter based on user roles
   * @param canPipe
   * @param anyAsset
   * @returns
   */
  getArvalAssetFilterStatusList(canPipe: CanPipe, anyAsset: DataAccessType): { id: Asset.StatusEnum; name: string }[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
    };

    const assetStatusListObj: { id: Asset.StatusEnum; name: string }[] = [];

    if (userRoles.updateAllStatus) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        },
        {
          id: Asset.StatusEnum.Pending,
          name: $localize`Pending`,
        },
      );
    }

    return assetStatusListObj;
  }

  /**
   * VKB
   * Get asset status list for filter based on user roles
   * @param canPipe
   * @param anyAsset
   * @returns
   */
  getVKBAssetFilterStatusList(canPipe: CanPipe, anyAsset: DataAccessType, callFrom?: string): { id: Asset.StatusEnum; name: string }[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
      uploadedNAwaitingApproval: canPipe.transform(anyAsset, 'uploadednawaitingapproval'),
      publishedAssets: canPipe.transform(anyAsset, 'publishedassets'),
      archivedAssets: canPipe.transform(anyAsset, 'archived'),
    };

    const assetStatusListObj: { id: Asset.StatusEnum; name: string }[] = [];

    if (userRoles.updateAllStatus) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Uploaded,
          name: $localize`Uploaded`,
        },
        {
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        },
        {
          id: Asset.StatusEnum.Archived,
          name: $localize`Archived`,
        },
      );
    }

    if (userRoles.publishedAssets) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Approved,
        name: $localize`Approved`,
      });
    }

    if (callFrom === 'filterForm') {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Expired,
        name: $localize`Expired`,
      });
    }

    if (userRoles.archivedAssets) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Archived,
        name: $localize`Archived`,
      });
    }

    // Remove duplicates from the assetStatusListObj
    const uniqueObjectsMap = new Map<string, AnyType>();
    assetStatusListObj.forEach((obj) => {
      uniqueObjectsMap.set(obj.id, obj);
    });
    const uniqueAssetStatusList = Array.from(uniqueObjectsMap.values());

    return uniqueAssetStatusList;
  }

  /**
   * SEAT
   * Get asset status list for filter based on user roles
   * @param canPipe
   * @param anyAsset
   * @returns
   */
  getSEATAssetFilterStatusList(canPipe: CanPipe, anyAsset: DataAccessType): { id: Asset.StatusEnum; name: string }[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
      uploadedNAwaitingApproval: canPipe.transform(anyAsset, 'uploadednawaitingapproval'),
      publishedAssets: canPipe.transform(anyAsset, 'publishedassets'),
    };
    const assetStatusListObj: { id: Asset.StatusEnum; name: string }[] = [];
    if (userRoles.updateAllStatus) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Uploaded,
          name: $localize`Uploaded`,
        },
        {
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        },
      );
    }
    if (userRoles.uploadedNAwaitingApproval) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Uploaded,
        name: $localize`Uploaded`,
      });
    }
    if (userRoles.publishedAssets) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Approved,
        name: $localize`Approved`,
      });
    }
    // Remove duplicates from the assetStatusListObj
    const uniqueObjectsMap = new Map<string, AnyType>();
    assetStatusListObj.forEach((obj) => {
      uniqueObjectsMap.set(obj.id, obj);
    });
    const uniqueAssetStatusList = Array.from(uniqueObjectsMap.values());

    return uniqueAssetStatusList;
  }

  getStandardAssetFilterStatusList(canPipe: CanPipe, anyAsset: DataAccessType, callFrom?: string): { id: Asset.StatusEnum; name: string }[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
      publishedAssets: canPipe.transform(anyAsset, 'publishedassets'),
      archived: canPipe.transform(anyAsset, 'archived'),
      draft: canPipe.transform(anyAsset, 'draft'),
    };
    const assetStatusListObj: { id: Asset.StatusEnum; name: string }[] = [];
    if (userRoles.updateAllStatus) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Approved,
        name: $localize`Approved`,
      });
    }
    if (userRoles.publishedAssets) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Approved,
        name: $localize`Approved`,
      });
    }
    if (userRoles.archived) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Archived,
        name: $localize`Archived`,
      });
    }
    if (userRoles.draft) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Draft,
        name: $localize`Draft`,
      });
    }
    // Remove duplicates from the assetStatusListObj
    const uniqueObjectsMap = new Map<string, AnyType>();
    assetStatusListObj.forEach((obj) => {
      uniqueObjectsMap.set(obj.id, obj);
    });
    const uniqueAssetStatusList = Array.from(uniqueObjectsMap.values());

    return uniqueAssetStatusList;
  }

  /**
   * Lufthansa
   * Get asset status list for filter based on user roles
   * @param canPipe
   * @param anyAsset
   * @returns
   */
  getLufthansaAssetFilterStatusList(canPipe: CanPipe, anyAsset: DataAccessType): { id: Asset.StatusEnum; name: string }[] {
    const userRoles = {
      updateAllStatus: canPipe.transform(anyAsset, 'update.allstatus'),
      uploadedNAwaitingApproval: canPipe.transform(anyAsset, 'uploadednawaitingapproval'),
      publishedAssets: canPipe.transform(anyAsset, 'publishedassets'),
    };

    const assetStatusListObj: { id: Asset.StatusEnum; name: string }[] = [];

    if (userRoles.updateAllStatus) {
      assetStatusListObj.push(
        {
          id: Asset.StatusEnum.Approved,
          name: $localize`Approved`,
        },
        {
          id: Asset.StatusEnum.Archived,
          name: $localize`Archived`,
        },
        {
          id: Asset.StatusEnum.Uploaded,
          name: $localize`Uploaded`,
        },
      );
    }

    if (userRoles.publishedAssets) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Approved,
        name: $localize`Approved`,
      });
    }

    if (userRoles.uploadedNAwaitingApproval) {
      assetStatusListObj.push({
        id: Asset.StatusEnum.Uploaded,
        name: $localize`Uploaded`,
      });
    }

    // Remove duplicates from the assetStatusListObj
    const uniqueObjectsMap = new Map<string, AnyType>();
    assetStatusListObj.forEach((obj) => {
      uniqueObjectsMap.set(obj.id, obj);
    });
    const uniqueAssetStatusList = Array.from(uniqueObjectsMap.values());

    return uniqueAssetStatusList;
  }

  constructMakeOSUsageRights(rawFormValues: AnyType, deletedBuyoutRightIds?: string[], deletedBuyoutRightTerritoryObj?: Map<string, string[]>) {
    const toReturn: AnyType = {};
    const makeBuyoutRights = this.transformAndFilterKeys(JSON.parse(JSON.stringify(rawFormValues.usageRightsGeneralInformation || {})), ['notes']);
    const buyoutRightsFormArray = JSON.parse(JSON.stringify(rawFormValues.usageRightsGeneralInformation?.buyoutRightsFormArray || {}));
    const buyoutRightsFields: Partial<BuyoutRightFields>[] = [];
    for (const rights in buyoutRightsFormArray) {
      const territoriesFields: Partial<Territories>[] = [];
      for (const territories in buyoutRightsFormArray[rights]['territoriesFormArray']) {
        territoriesFields.push(this.transformAndFilterKeys(buyoutRightsFormArray[rights]['territoriesFormArray'][territories], []));
      }
      const buyoutRight = this.transformAndFilterKeys(buyoutRightsFormArray[rights], []);
      if (territoriesFields.length) {
        buyoutRight['territories'] = territoriesFields;
        delete buyoutRight['territoriesFormArray'];
      }
      buyoutRightsFields.push(buyoutRight);
    }

    if (buyoutRightsFields.length !== 0) {
      makeBuyoutRights['buyoutRights'] = buyoutRightsFields;
      delete makeBuyoutRights['buyoutRightsFormArray'];
    }
    if (!this.commonUtilityLib.isEmptyObject(makeBuyoutRights)) toReturn.makeBuyoutRights = [makeBuyoutRights];
    if (deletedBuyoutRightIds && deletedBuyoutRightIds.length) toReturn.deletedBuyoutRightIds = deletedBuyoutRightIds;
    if (deletedBuyoutRightTerritoryObj && deletedBuyoutRightTerritoryObj.size) toReturn.deletedBuyoutRightTerritoryObj = deletedBuyoutRightTerritoryObj;
    if (!this.commonUtilityLib.isEmptyObject(toReturn)) return toReturn;
    return undefined;
  }

  constructMakeOSUsageRightsForUpdates(rawFormValues: AnyType, assetLists: AssetUIModel, buyoutRightEditId: string) {
    const buyoutRightsFormArray = JSON.parse(JSON.stringify(rawFormValues.usageRightsGeneralInformation?.buyoutRightsFormArray || {}));
    const currentMakeBuyoutRightsIndex = assetLists.makeBuyoutRights?.findIndex((right) => right._id === buyoutRightEditId) as number;
    const transformedBuyoutRights: BuyoutRightsPayload = { buyoutRights: {}, _id: buyoutRightEditId };

    for (const rights in buyoutRightsFormArray) {
      const rightIndex = +rights;
      const assetBuyoutRight = (assetLists.makeBuyoutRights?.[currentMakeBuyoutRightsIndex] as AnyType).buyoutRights[rightIndex];

      const { territoriesFormArray, ...restFormFields } = buyoutRightsFormArray[rightIndex];

      if (!assetBuyoutRight) {
        const transformedTerritories = Object.values(territoriesFormArray).reduce((acc: TerritoryMap, territoryItem: AnyType, index) => {
          acc[index] = this.transformAndFilterKeys(territoryItem);
          return acc;
        }, {} as TerritoryMap);
        transformedBuyoutRights.buyoutRights[rightIndex] = {
          ...restFormFields,
          territories: transformedTerritories,
        };
        continue;
      }

      const { territories: _, ...restBuyoutRight } = assetBuyoutRight;

      if (!transformedBuyoutRights.buyoutRights[rightIndex]) {
        transformedBuyoutRights.buyoutRights[rightIndex] = {
          _id: assetBuyoutRight._id,
          ...restBuyoutRight,
          ...restFormFields,
        };
      }

      if (territoriesFormArray) {
        if (!transformedBuyoutRights.buyoutRights[rightIndex].territories) {
          transformedBuyoutRights.buyoutRights[rightIndex].territories = {};
        }

        const territoriesMap = transformedBuyoutRights.buyoutRights[rightIndex].territories;

        for (const territories in territoriesFormArray) {
          const territoryIndex = +territories;
          const territory = assetBuyoutRight.territories[territoryIndex];
          // Check if the territory exists in the original assetBuyoutRight
          // If it doesn't exist, create a new territory object

          if (!territory) {
            if (territoriesMap) {
              territoriesMap[territoryIndex] = {
                ...this.transformAndFilterKeys(territoriesFormArray[territoryIndex], []),
              };
            }
            continue;
          }
          // If it exists, update the existing territory object
          // with the new values from the territoriesFormArray
          if (territoriesMap) {
            territoriesMap[territoryIndex] = {
              _id: territory._id,
              ...territory,
              ...this.transformAndFilterKeys(territoriesFormArray[territoryIndex], []),
            };
          }
        }
      }
    }
    return transformedBuyoutRights;
  }

  constructMakeOSUsageRightsForMultiEdit(rawFormValues: AnyType, assetLists: AssetUIModel, buyoutRightEditId: string[]) {
    const buyoutRightsFormArray = JSON.parse(JSON.stringify(rawFormValues.usageRightsGeneralInformation?.buyoutRightsFormArray || {}));
    const currentMakeBuyoutRightsIndex = assetLists.makeBuyoutRights?.findIndex(
      (right) => Array.isArray(right._id) && right._id.some((id: string) => buyoutRightEditId.includes(id)),
    ) as number;
    const transformedBuyoutRights: BuyoutRightsMultiEditPayload = { buyoutRights: {}, _id: buyoutRightEditId };

    for (const rights in buyoutRightsFormArray) {
      const rightIndex = +rights;
      const assetBuyoutRight = (assetLists.makeBuyoutRights?.[currentMakeBuyoutRightsIndex] as AnyType).buyoutRights[rightIndex];

      const { territoriesFormArray, ...restFormFields } = buyoutRightsFormArray[rightIndex];

      if (!assetBuyoutRight) {
        const transformedTerritories = Object.values(territoriesFormArray).reduce((acc: TerritoryMap, territoryItem: AnyType, index) => {
          acc[index] = this.transformAndFilterKeys(territoryItem);
          return acc;
        }, {} as TerritoryMap);
        transformedBuyoutRights.buyoutRights[rightIndex] = {
          ...restFormFields,
          territories: transformedTerritories,
        };
        continue;
      }

      if (!transformedBuyoutRights.buyoutRights[rightIndex]) {
        transformedBuyoutRights.buyoutRights[rightIndex] = {
          _id: assetBuyoutRight._id,
          ...restFormFields,
        };
      }

      if (territoriesFormArray) {
        if (!transformedBuyoutRights.buyoutRights[rightIndex].territories) {
          transformedBuyoutRights.buyoutRights[rightIndex].territories = {};
        }

        const territoriesMap = transformedBuyoutRights.buyoutRights[rightIndex].territories;

        for (const territories in territoriesFormArray) {
          const territoryIndex = +territories;
          const territory = assetBuyoutRight.territories[territoryIndex];
          // Check if the territory exists in the original assetBuyoutRight
          // If it doesn't exist, create a new territory object
          if (!territory) {
            if (territoriesMap) {
              territoriesMap[territoryIndex] = {
                ...this.transformAndFilterKeys(territoriesFormArray[territoryIndex], []),
              };
            }
            continue;
          }

          // If it exists, update the existing territory object
          // with the new values from the territoriesFormArray
          if (territoriesMap) {
            territoriesMap[territoryIndex] = {
              _id: territory._id,
              //...territory,
              ...this.transformAndFilterKeys(territoriesFormArray[territoryIndex], []),
            };
          }
        }
      }
    }
    return transformedBuyoutRights;
  }
}
